import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toLocalDate } from '@/utils/dateHelpers';
import { StatusBadge } from './StatusBadge';
import { TaskCustomFieldsSection } from './TaskCustomFieldsSection';
import { TaskSubtasksSection } from './TaskSubtasksSection';
import { TaskCollaborators } from './TaskCollaborators';
import { TaskComments } from './TaskComments';
import { useTasks } from '@/hooks/useTasks';
import { useTaskSections } from '@/hooks/useTaskSections';
import { useCompanyUsers } from '@/hooks/useCompanyUsers';
import { useStableAuth } from '@/hooks/useStableAuth';
import { Calendar as CalendarIcon, AlertCircle, User, Flag, Info, Folder, FolderOpen, Package, Link2, ExternalLink, ShieldCheck, Paperclip, Download, FileIcon, Loader2, Plus, X, ClipboardList, Tag } from 'lucide-react';
import { useTaskDependencies } from '@/hooks/useTaskDependencies';
import { useTaskAttachments } from '@/hooks/useTaskAttachments';
import { supabase } from '@/integrations/supabase/client';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Task, TaskPriority, TaskStatus, TaskAttachment } from '@/types/task';
import { UserAvatar } from '@/components/ui/user-avatar';
import { toast } from 'sonner';
import { isSystemTask as checkSystemTask, getOriginModule, getOriginRoute } from '@/constants/taskOriginLabels';

interface TaskDetailSheetProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}


const priorityLabels: Record<TaskPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente'
};

const priorityFlagColors: Record<TaskPriority, string> = {
  low: 'text-blue-400',
  medium: 'text-amber-500',
  high: 'text-orange-500',
  urgent: 'text-red-600',
};

const priorityDotColors: Record<TaskPriority, string> = {
  low: 'bg-blue-400',
  medium: 'bg-amber-500',
  high: 'bg-orange-500',
  urgent: 'bg-red-600',
};

export const TaskDetailSheet = ({ task, open, onOpenChange }: TaskDetailSheetProps) => {
  const navigate = useNavigate();
  const { updateTask, toggleTaskStatus } = useTasks();
  const { sections } = useTaskSections();
  const { users } = useCompanyUsers();
  const { userId } = useStableAuth();
  const { dependencies, hasActiveDependencies } = useTaskDependencies(task?.id || '', open && !!task);
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    attachments,
    uploadAttachment,
    isUploading,
    deleteAttachment,
  } = useTaskAttachments(task?.id ?? null);

  // Sync local state when task changes (fix stale state)
  useEffect(() => {
    setTitle(task?.title || '');
    setDescription(task?.description || '');
    setEditingTitle(false);
    setEditingDescription(false);
  }, [task?.id]);

  const systemTask = checkSystemTask(task);
  const originModule = getOriginModule(task);
  const originRoute = getOriginRoute(task);

  // Derive creator from existing users array (no N+1 query)
  const creator = users.find(u => u.id === task?.created_by);
  const creatorName = creator?.full_name ?? null;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    try {
      for (const file of files) {
        await uploadAttachment(file);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (attachment: TaskAttachment) => {
    // Abrir aba ANTES do await — user gesture obrigatório
    const tab = window.open('', '_blank');
    try {
      const { data, error } = await supabase.storage
        .from('task-attachments')
        .createSignedUrl(attachment.storage_path, 120);
      if (error || !data) throw error ?? new Error('No URL');
      if (tab) {
        tab.location.href = data.signedUrl;
      } else {
        // Fallback: aba bloqueada pelo browser
        window.location.href = data.signedUrl;
      }
    } catch (err) {
      if (tab) tab.close();
      console.error('Download failed:', err);
    }
  };

  const handleDeleteAttachment = async (id: string, storagePath: string) => {
    try {
      await deleteAttachment({ attachmentId: id, storagePath });
    } catch (err) {
      console.error('Delete attachment failed:', err);
    }
  };

  if (!task) return null;

  const handleTitleSave = async () => {
    if (title !== task.title) {
      await updateTask(task.id, { title });
    }
    setEditingTitle(false);
  };

  const handleDescriptionSave = async () => {
    if (description !== task.description) {
      await updateTask(task.id, { description });
    }
  };

  const handleStatusChange = async (newStatus: TaskStatus) => {
    if (systemTask) {
      toast.info(`Status só pode ser alterado no módulo de ${originModule}.`);
      return;
    }
    await updateTask(task.id, { status: newStatus });
  };

  const handlePriorityChange = async (priority: TaskPriority) => {
    if (systemTask) return;
    await updateTask(task.id, { priority });
  };

  const handleAssigneeChange = async (userId: string) => {
    if (systemTask) return;
    await updateTask(task.id, { assigned_to: userId || undefined });
  };

  const handleDueDateChange = async (date: Date | undefined) => {
    if (systemTask) return;
    await updateTask(task.id, { due_date: date?.toISOString().split('T')[0] });
  };

  const handleSectionChange = async (sectionId: string) => {
    if (systemTask) return;
    await updateTask(task.id, { 
      section_id: sectionId === 'none' ? null : sectionId 
    });
  };

  const handleDeepLink = () => {
    if (originRoute) {
      onOpenChange(false);
      navigate(originRoute);
    }
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const input = e.currentTarget;
    const trimmed = input.value.trim();
    if (!trimmed || (task.tags ?? []).includes(trimmed)) {
      input.value = '';
      return;
    }
    updateTask(task.id, { tags: [...(task.tags ?? []), trimmed] });
    input.value = '';
  };

  const handleRemoveTag = (tag: string) => {
    updateTask(task.id, { tags: (task.tags ?? []).filter((t) => t !== tag) });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        className="w-full sm:max-w-2xl overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <div className="flex items-start gap-3">
            <Checkbox
              checked={task.status === 'completed'}
              onCheckedChange={() => {
                if (systemTask) {
                  toast.info(`Esta tarefa está vinculada ao módulo de ${originModule}. Conclua no módulo de origem.`);
                  return;
                }
                toggleTaskStatus(task.id, task.status);
              }}
              className="mt-1"
              disabled={systemTask}
            />
            {editingTitle && !systemTask ? (
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => e.key === 'Enter' && handleTitleSave()}
                autoFocus
                className="flex-1"
              />
            ) : (
              <SheetTitle 
                className={cn("flex-1", !systemTask && "cursor-pointer hover:text-primary")}
                onClick={() => !systemTask && setEditingTitle(true)}
              >
                {task.title}
              </SheetTitle>
            )}
          </div>

          {/* Informação do criador */}
          {creator && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
              <span>Criado por</span>
              <UserAvatar
                avatarUrl={creator.avatar_url}
                name={creator.full_name}
                size="sm"
              />
              <span className="font-medium">{creator.full_name}</span>
              <span>•</span>
              <span>
                {task.created_at
                  ? formatDistanceToNow(new Date(task.created_at), { addSuffix: true, locale: ptBR })
                  : 'há pouco tempo'
                }
              </span>
            </div>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Aviso para tarefas de sistema */}
          {systemTask && (
            <Alert className="bg-blue-500/10 border-blue-500/20">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                <div className="flex items-center justify-between gap-2">
                  <span>
                    Esta tarefa é gerada automaticamente pelo módulo de <strong>{originModule}</strong>. 
                    Campos críticos são somente leitura.
                  </span>
                  {originRoute && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDeepLink}
                      className="shrink-0 gap-1.5"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Ver {originModule === 'Entregas' ? 'Entrega' : originModule === 'Vendas' ? 'Venda' : 'Origem'}
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Status e Prioridade */}
          <div className="flex gap-3">
            <StatusBadge 
              status={task.status} 
              onStatusChange={handleStatusChange}
              editable={!systemTask}
            />
            <Select value={task.priority} onValueChange={handlePriorityChange} disabled={systemTask}>
              <SelectTrigger className="w-[150px]">
                <Flag className={`h-4 w-4 mr-2 ${priorityFlagColors[task.priority as TaskPriority] ?? 'text-muted-foreground'}`} />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(priorityLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full inline-block ${priorityDotColors[key as TaskPriority] ?? 'bg-muted-foreground'}`} />
                      {label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Informações */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Responsável
              </label>
              <Select 
                value={task.assigned_to || 'unassigned'} 
                onValueChange={handleAssigneeChange}
                disabled={systemTask}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sem responsável" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Sem responsável</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <UserAvatar
                          avatarUrl={user.avatar_url}
                          name={user.full_name}
                          size="sm"
                        />
                        <span>{user.full_name}</span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Aviso quando tarefa privada for atribuída */}
          {task?.is_private && task?.assigned_to && task.assigned_to !== userId && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Esta tarefa se tornará visível para o responsável atribuído
              </AlertDescription>
            </Alert>
          )}

          {/* Colaboradores */}
          <TaskCollaborators taskId={task.id} />

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                Data de Conclusão
              </label>
              {systemTask ? (
                <Button variant="outline" className="w-full justify-start" disabled>
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {task.due_date 
                    ? format(toLocalDate(task.due_date), "dd 'de' MMMM, yyyy", { locale: ptBR })
                    : 'Sem data definida'
                  }
                </Button>
              ) : (
                <Popover modal={false}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      aria-label={task.due_date ? `Data de conclusão: ${format(toLocalDate(task.due_date), "dd 'de' MMMM, yyyy", { locale: ptBR })}` : "Selecionar data de conclusão"}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {task.due_date 
                        ? format(toLocalDate(task.due_date), "dd 'de' MMMM, yyyy", { locale: ptBR })
                        : 'Selecione uma data'
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-auto p-0 pointer-events-auto z-[9999] bg-background" 
                    align="start"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <Calendar
                      mode="single"
                      selected={task.due_date ? toLocalDate(task.due_date) : undefined}
                      onSelect={handleDueDateChange}
                      locale={ptBR}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Projeto — visível apenas quando a tarefa pertence a pelo menos um projeto */}
            {task.task_project_memberships && task.task_project_memberships.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FolderOpen className="h-4 w-4 text-muted-foreground" />
                  <span>Projeto</span>
                </div>
                <div className="space-y-1">
                  {task.task_project_memberships.map((membership, idx) => (
                    membership.project ? (
                      <div
                        key={idx}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/40 text-sm"
                      >
                        <FolderOpen className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium">{membership.project.name}</span>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            )}

            {/* Seção */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Folder className="h-4 w-4" />
                Seção
              </label>
              <Select 
                value={task.section_id || 'none'} 
                onValueChange={handleSectionChange}
                disabled={systemTask}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sem seção" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem seção</SelectItem>
                  {sections.map(section => (
                    <SelectItem key={section.id} value={section.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: section.color }}
                        />
                        <span>{section.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Descrição */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Descrição
            </label>
            {editingDescription && !systemTask ? (
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => {
                  handleDescriptionSave();
                  setEditingDescription(false);
                }}
                placeholder="Adicione uma descrição..."
                rows={4}
                autoFocus
                className="resize-none"
              />
            ) : (
              <div 
                onClick={() => !systemTask && setEditingDescription(true)}
                className={cn(
                  "prose prose-sm dark:prose-invert max-w-none p-3 border rounded-md min-h-[100px] text-sm",
                  systemTask ? "cursor-default bg-muted/30" : "cursor-pointer hover:bg-muted/50"
                )}
              >
                {description ? (
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                      li: ({ children }) => <li className="mb-1">{children}</li>,
                    }}
                  >
                    {description}
                  </ReactMarkdown>
                ) : (
                  <span className="text-muted-foreground">
                    {systemTask ? 'Sem descrição' : 'Clique para adicionar uma descrição...'}
                  </span>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Subtarefas */}
          <TaskSubtasksSection taskId={task.id} readOnly={systemTask} />

          <Separator />

          {/* Campos Personalizados */}
          <TaskCustomFieldsSection taskId={task.id} readOnly={systemTask} />

          {/* Tags */}
          {((task.tags && task.tags.length > 0) || !systemTask) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span>Tags</span>
              </div>
              <div className="flex flex-wrap gap-1.5 items-center min-h-[28px]">
                {(task.tags ?? []).map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="gap-1 text-xs pr-1"
                  >
                    {tag}
                    {!systemTask && (
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-0.5 hover:text-destructive transition-colors"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </Badge>
                ))}
                {!systemTask && (
                  <input
                    type="text"
                    placeholder="+ Adicionar tag"
                    className="h-6 text-xs bg-transparent border-none outline-none
                               placeholder:text-muted-foreground/50 min-w-[90px]"
                    onKeyDown={handleAddTag}
                  />
                )}
              </div>
            </div>
          )}

          <Separator />

          {/* Dependências */}
          {dependencies.length > 0 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  Depende de
                </label>
                <div className="flex flex-wrap gap-2">
                  {dependencies.map(dep => (
                    <Badge
                      key={dep.id}
                      variant={dep.blocked_by_task?.status === 'completed' ? 'secondary' : 'outline'}
                      className="gap-1.5"
                    >
                      {dep.blocked_by_task?.title || 'Tarefa removida'}
                      <span className="text-xs opacity-70">
                        {dep.blocked_by_task?.status === 'completed' ? '✓' : '⏳'}
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Seção Anexos */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Paperclip className="h-4 w-4 text-muted-foreground" />
                <span>Anexos</span>
                {attachments.length > 0 && (
                  <span className="text-xs text-muted-foreground">({attachments.length})</span>
                )}
              </div>
              {!systemTask && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Plus className="h-3.5 w-3.5" />
                  }
                  {isUploading ? 'Enviando...' : 'Adicionar'}
                </Button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              onChange={handleFileSelect}
            />

            {attachments.length === 0 ? (
              <p className="text-xs text-muted-foreground py-1 pl-1">
                Nenhum anexo adicionado
              </p>
            ) : (
              <div className="space-y-1">
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center gap-2 p-2 rounded-md bg-muted/40 hover:bg-muted/60 group"
                  >
                    <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{attachment.file_name}</p>
                      {attachment.file_size != null && (
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(attachment.file_size)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleDownload(attachment)}
                        title="Baixar"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {!systemTask && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:text-destructive"
                          onClick={() => handleDeleteAttachment(attachment.id, attachment.storage_path)}
                          title="Remover"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Evento de criação */}
          {task.created_at && (
            <div className="flex items-center gap-3 pb-3 border-b border-border/30">
              <div className="h-7 w-7 rounded-full bg-muted/60 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {creatorName ?? 'Usuário'}
                  </span>
                  {' '}criou esta tarefa{' '}
                  <span className="text-xs">
                    · {formatDistanceToNow(new Date(task.created_at), {
                        addSuffix: true,
                        locale: ptBR
                      })}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Comentários */}
          <TaskComments taskId={task.id} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
